import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["type"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useCallback, useContext, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { createUseStyles, useTheme } from '@Sage/hoi-poi-ui';
import { CSSTransition } from 'react-transition-group';
import { SidebarContext, SidebarProvider, SidebarConsumer } from "./components/SidebarContext";
import LogoSidebar from "./components/LogoSidebar";
import ToggleSidebar from "./components/ToggleSidebar";
import MenuItem from "./components/MenuItem";
import MenuTooltip from "./components/MenuTooltip";
import styles from "./styles/index";
const useStyles = createUseStyles(styles, 'Sidebar');
const Sidebar = /*#__PURE__*/memo(_ref => {
  let {
    className: classNameProp,
    schema,
    onMatchActive,
    onExpand,
    getPostComponent,
    customColor,
    customLogo,
    currentHref = '',
    delayHover = 800,
    delayTooltip = 800
  } = _ref;
  const {
    appName,
    homeLink,
    menuItems
  } = schema;
  const {
    isExpanded,
    isMobile,
    setExpanded
  } = useContext(SidebarContext);
  const [hover, setHover] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltip, setTooltip] = useState({});
  const tooltipTimeoutRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const theme = useTheme();
  const classes = useStyles();
  const rootClassName = classnames(classes.root, {
    [classes.isExpanded]: isExpanded,
    [classes.isHover]: hover || isMobile
  }, classNameProp);
  const handleClearTooltip = useCallback(() => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, delayTooltip);
  }, [delayTooltip]);
  const handleShowTooltip = useCallback(data => {
    clearTimeout(tooltipTimeoutRef.current);
    setTooltip(data);
    setShowTooltip(true);
  }, []);
  const handleHover = useCallback(function (hover) {
    if (hover === void 0) {
      hover = true;
    }
    clearTimeout(hoverTimeoutRef.current);
    const timeout = hover ? 0 : delayHover;
    hoverTimeoutRef.current = setTimeout(() => {
      setHover(hover);
      if (!hover) setShowTooltip(false);
    }, timeout);
  }, [delayHover, setHover]);
  const handleToggle = useCallback(() => {
    if (onExpand) onExpand();
    setExpanded(isExpanded => !isExpanded);
  }, [setExpanded, onExpand]);
  const getMenuItem = useCallback((type, itemProps) => ({
    divider: /*#__PURE__*/React.createElement("span", {
      className: classes.divider
    }),
    link: /*#__PURE__*/React.createElement(MenuItem, itemProps)
  })[type], [classes.divider]);
  const getMenuItems = useMemo(() => menuItems.map((item, index) => {
    const {
        type
      } = item,
      otherProps = _objectWithoutProperties(item, _excluded);
    const selected = onMatchActive ? onMatchActive(item) : currentHref.includes(item.link) || item.link === `..${window.location.pathname}`;
    const itemProps = _objectSpread({
      isActive: selected || false,
      onMouseEnter: handleShowTooltip,
      onMouseLeave: handleClearTooltip
    }, otherProps);
    const Item = () => getMenuItem(type, itemProps);
    return /*#__PURE__*/React.createElement(Item, {
      key: `item-${index}`
    });
  }), [currentHref, getMenuItem, handleClearTooltip, handleShowTooltip, menuItems, onMatchActive]);
  const finalColor = customColor && customColor !== '' ? customColor : theme.colors.primary.black;
  return /*#__PURE__*/React.createElement("nav", {
    className: rootClassName,
    onMouseEnter: e => handleHover(),
    onMouseLeave: e => handleHover(false)
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      backgroundColor: finalColor
    },
    className: classes.inner
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.brand
  }, /*#__PURE__*/React.createElement(LogoSidebar, {
    appName: appName,
    homeLink: homeLink,
    isExpanded: isExpanded,
    customLogo: customLogo
  })), !getPostComponent && /*#__PURE__*/React.createElement("div", {
    className: classes.menu
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.menuInner
  }, getMenuItems)), getPostComponent && /*#__PURE__*/React.createElement("div", {
    className: classes.menuContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.menu
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.menuInner
  }, getMenuItems)), /*#__PURE__*/React.createElement("div", {
    className: classes.postComponent
  }, getPostComponent({
    isExpanded
  })))), /*#__PURE__*/React.createElement(ToggleSidebar, {
    className: classes.toggleSidebar,
    isExpanded: isExpanded,
    isVisible: hover || isMobile,
    onClick: handleToggle,
    customColor: customColor
  }), !isExpanded && !isMobile && /*#__PURE__*/React.createElement(CSSTransition, {
    classNames: "tooltip-transition",
    in: showTooltip,
    timeout: 400,
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement(MenuTooltip, {
    name: tooltip === null || tooltip === void 0 ? void 0 : tooltip.name,
    top: tooltip === null || tooltip === void 0 ? void 0 : tooltip.elTop
    //customColor={finalColor}
    ,
    className: classnames(classes.menuTooltip, classes.tooltipTransition)
  })));
});
process.env.NODE_ENV !== "production" ? Sidebar.propTypes = {
  className: PropTypes.string,
  currentHref: PropTypes.string,
  delayHover: PropTypes.number,
  delayTooltip: PropTypes.number,
  onMatchActive: PropTypes.func,
  schema: PropTypes.shape({
    appName: PropTypes.string,
    homeLink: PropTypes.string,
    menuItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({
      type: PropTypes.oneOf(['divider'])
    }), PropTypes.shape({
      type: PropTypes.oneOf(['link']),
      icon: PropTypes.string,
      isNew: PropTypes.bool,
      isNewTab: PropTypes.bool,
      link: PropTypes.string,
      name: PropTypes.string
    })]))
  }).isRequired
} : void 0;
export default Sidebar;
export { SidebarContext, SidebarProvider, SidebarConsumer, ToggleSidebar };